import React, {useState} from 'react';
import {useSortable} from '@dnd-kit/sortable';
import {CSS} from '@dnd-kit/utilities';
import {
    Box,
    Paper,
    Typography,
    TextField,
    IconButton,
    TextareaAutosize,
    Button,
    Menu,
    MenuItem,
    useTheme,
} from '@mui/material';
import {
    DragHandle as DragHandleIcon,
    Delete as DeleteIcon,
    Add as AddIcon,
    ExpandMore as ExpandMoreIcon,
    ExpandLess as ExpandLessIcon,
} from '@mui/icons-material';

// Defined question types constants
const QUESTION_TYPES = [
    {type: 'string', label: 'Short Text'},
    {type: 'text', label: 'Long Text'},
    {type: 'number', label: 'Number'},
    {type: 'date', label: 'Date'},
    {type: 'single_choice', label: 'Single Choice'},
    {type: 'multi_choice', label: 'Multiple Choice'}
];

export const Section = ({
                            id,
                            section,
                            children,
                            onUpdate,
                            onDelete,
                            onAddQuestion,
                            isOverlay = false,
                            isAnySectionDragging = false,
                            editable = true
                        }) => {
    const theme = useTheme(); // Add theme hook
    const [anchorEl, setAnchorEl] = useState(null);
    const [isCollapsed, setIsCollapsed] = useState(false);
    const open = Boolean(anchorEl);

    const {
        attributes, listeners, setNodeRef, transform, transition, isDragging
    } = useSortable({
        id,
        disabled: !editable // Disable drag when not editable
    });

    const style = {
        transform: CSS.Transform.toString(transform),
        transition,
        opacity: isDragging ? 0.5 : 1,
        zIndex: isOverlay ? 1000 : 'auto',
        borderStyle: editable ? 'dashed' : 'solid'
    };

    const handleClick = (event) => {
        if (editable) {
            setAnchorEl(event.currentTarget);
        }
    };

    const handleClose = () => {
        setAnchorEl(null);
    };

    const handleAddQuestion = (type) => {
        onAddQuestion(section.id, type);
        handleClose();
    };

    const toggleCollapse = () => {
        setIsCollapsed(!isCollapsed);
    };

    // Determine if content should be shown (not dragging, not in overlay, not collapsed)
    const showContent = !isDragging && !isOverlay && !isAnySectionDragging && !isCollapsed;

    return (
        <Paper
            variant="outlined"
            ref={setNodeRef}
            style={style}
            elevation={isOverlay ? 8 : 2}
            sx={{
                mb: 3,
                p: 2,
                position: 'relative',
                backgroundColor: editable
                    ? 'inherit'
                    : theme.palette.mode === 'dark'
                        ? 'rgba(255, 255, 255, 0.05)'
                        : 'rgba(0, 0, 0, 0.03)',
            }}
            {...(editable ? attributes : {})}
        >
            {/* Section Header - always visible with repositioned buttons */}
            <Box display="flex" alignItems="center" mb={1}>
                {/* Drag handle on the left */}
                {editable && (
                    <IconButton
                        {...listeners}
                        sx={{mr: 1, cursor: 'grab'}}
                        size="small"
                    >
                        <DragHandleIcon/>
                    </IconButton>
                )}

                {/* Title in the middle */}
                <Box flexGrow={1}>
                    {editable ? (
                        <TextField
                            value={section.title}
                            onChange={(e) => onUpdate(section.id, {title: e.target.value})}
                            placeholder="Section title"
                            variant="standard"
                            fullWidth
                            InputProps={{
                                style: {fontSize: '1.25rem', fontWeight: 'bold'}
                            }}
                        />
                    ) : (
                        <Typography variant="h5" sx={{fontWeight: 'bold'}}>
                            {section.title}
                        </Typography>
                    )}
                </Box>

                {/* Buttons on the right */}
                {editable && (
                    <Box>
                        {/* Collapse/Expand button */}
                        <IconButton
                            onClick={toggleCollapse}
                            color="primary"
                            size="small"
                        >
                            {isCollapsed ? <ExpandMoreIcon/> : <ExpandLessIcon/>}
                        </IconButton>

                        {/* Delete button */}
                        <IconButton
                            onClick={() => onDelete(section.id)}
                            color="error"
                            size="small"
                        >
                            <DeleteIcon/>
                        </IconButton>
                    </Box>
                )}
            </Box>

            {/* Only show full content when not collapsed, not dragging, and no section is being dragged */}
            {showContent && (
                <>
                    {/* Section Description */}
                    {editable ? (
                        <TextareaAutosize
                            value={section.description}
                            onChange={(e) => onUpdate(section.id, {description: e.target.value})}
                            placeholder="Section description"
                            minRows={2}
                            style={{
                                width: '100%',
                                padding: '8px',
                                marginBottom: '16px',
                                border: '1px solid #ccc',
                                borderRadius: '4px',
                                fontFamily: 'inherit',
                                fontSize: 'inherit',
                                backgroundColor: theme.palette.mode === 'dark' ? theme.palette.background.paper : 'white',
                                color: theme.palette.text.primary,
                            }}
                        />
                    ) : section.description ? (
                        <Typography
                            variant="body1"
                            sx={{
                                mb: 2,
                                whiteSpace: 'pre-wrap' // Preserve line breaks
                            }}
                        >
                            {section.description}
                        </Typography>
                    ) : null}

                    {/* Questions List */}
                    <Box sx={{position: 'relative'}}>
                        {children}
                    </Box>

                    {/* Add Question Button - only in edit mode */}
                    {editable && (
                        <Box sx={{mt: 2, display: 'flex', justifyContent: 'center'}}>
                            <Button
                                variant="outlined"
                                color="primary"
                                startIcon={<AddIcon/>}
                                onClick={handleClick}
                                sx={{
                                    borderRadius: '8px',
                                    px: 3
                                }}
                            >
                                Add Question
                            </Button>
                            <Menu
                                anchorEl={anchorEl}
                                open={open}
                                onClose={handleClose}
                            >
                                {QUESTION_TYPES.map((item) => (
                                    <MenuItem
                                        key={item.type}
                                        onClick={() => handleAddQuestion(item.type)}
                                    >
                                        {item.label}
                                    </MenuItem>
                                ))}
                            </Menu>
                        </Box>
                    )}
                </>
            )}

            {/* When dragging, in overlay, collapsed, or any section is dragging, show a simplified view - FIXED BACKGROUND */}
            {(isDragging || isOverlay || isAnySectionDragging || isCollapsed) && (
                <Box sx={{
                    py: 1,
                    px: 2,
                    backgroundColor: theme.palette.mode === 'dark'
                        ? 'rgba(255, 255, 255, 0.05)'
                        : 'rgba(0, 0, 0, 0.03)',
                    borderRadius: 1,
                    // Highlight the current section if something is being dragged but it's not this one
                    border: isAnySectionDragging && !isDragging ? '2px dashed #1976d2' : 'none',
                }}>
                    <Typography variant="body2" color="text.secondary">
                        Section: {section.title} ({section.questions.length} questions)
                    </Typography>
                </Box>
            )}
        </Paper>
    );
};